import type { GridColDef, GridRowParams, GridRowScrollEndParams } from '@mui/x-data-grid-pro';
import type { BasePageProps } from '@shared/components/BasePageProps';
import type { FilterConfig } from '@shared/components/Filter.types';
import type { SortOption } from '@shared/components/Filters';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { GICSCode } from '@shared/domain/company/GICS.types';
import type { Company } from '../../authentication/types/types.auth';
import type { SearchCompaniesDTO } from './api/api';
import { AddCircleOutline, RateReview } from '@mui/icons-material';
import { Box, Chip, Tooltip } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import CompanyLogo from '@shared/components/CompanyLogo';
import Filters from '@shared/components/Filters';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';

import { CreateCompanyModal } from '@shared/components/Modals/CreateCompanyModal';
import CustomSnackbar from '@shared/components/Snackbar';
import { gicsTierRank } from '@shared/domain/company/gicsTierRank';
import { usePrefetchLogos } from '@shared/hooks/usePrefetchLogos';
import { useResponsive } from '@shared/hooks/useResponsive';
import { log } from '@shared/utils/log';
import { OnboardingAction, storeOnboardingIntent } from '@shared/utils/onboardingIntent';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import SEO from '@/shared/components/SEO';
import { UserContext } from '@/shared/contexts/UserContext';
import { trackEvent } from '../../../third-party/ga';
import SignUpOrLogInModal from '../../authentication/components/SignUpOrLogInModal';
import ReviewDialog from '../../review/components/ReviewDialog';
import { useReviewContext } from '../../review/context/ReviewContext';
import { fetchAllGicsCodes, fetchCompanies } from './api/api';
import { FilterCompanyEnum } from './types/company.types';
import './styles/company-search.scss';

interface CompanySearchPageProps extends BasePageProps {}

function CompanySearchPage(props: CompanySearchPageProps) {
  const [companies, setCompanies] = useState<Array<Company>>([]);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();
  const [openReviewDialog, setOpenReviewDialog] = useState(false);
  const [isAddCompany, setIsAddCompany] = useState(false);
  const [_isAddCompanyFlow, _setIsAddCompanyFlow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm] = useState('');

  // Infinite scrolling states
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [nextPageCompanies, setNextPageCompanies] = useState<Array<Company>>([]); // For predictive prefetching
  const PAGE_SIZE = 100; // Increased from 50 to reduce number of fetch requests

  const { user, updateUser, isLoggedIn } = useContext(UserContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [pendingFollowCompany, setPendingFollowCompany] = useState<Company | null>(null);
  const [authModalAction, setAuthModalAction] = useState<'follow' | 'review' | 'addCompany'>('follow');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 450);
  const { resetReviewState } = useReviewContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useResponsive();

  // State for filters
  const [industries, setIndustries] = useState<GICSCode[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize filters from URL search parameters first
  const getInitialFilters = () => {
    const searchParams = new URLSearchParams(location.search);
    const industryParam = searchParams.get('industry');

    return {
      industry: industryParam ? [industryParam] : [],
      companyName: '',
    };
  };

  const [currentFilters, setCurrentFilters] = useState<Record<string, string | string[]>>(getInitialFilters());
  const [sortBy, setSortBy] = useState('companyName');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');

  // Prefetch logos for better user experience (including next page companies)
  usePrefetchLogos(companies, {
    additionalCompanies: nextPageCompanies, // Include prefetched next page companies
    maxPrefetch: 80, // Increased to prefetch more companies
    delay: 100, // Reduced delay for faster prefetching
    enabled: !loading, // Only prefetch when not actively loading
  });

  // Update filters when URL search parameters change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const industryParam = searchParams.get('industry');

    setCurrentFilters((prev) => ({
      ...prev,
      industry: industryParam ? [industryParam] : [],
    }));
  }, [location.search]);

  // Fetch GICS codes on mount and set initialized when done
  useEffect(() => {
    const loadGicsCodes = async () => {
      try {
        const fetchedGicsCodes = await fetchAllGicsCodes();
        // Include all GICS tiers, not just industries
        setIndustries(fetchedGicsCodes);
      } catch (error) {
        log.error('Error fetching GICS codes:', error);
      } finally {
        // Set initialized to true regardless of success/failure
        setIsInitialized(true);
      }
    };

    loadGicsCodes();
  }, []);

  const [filters] = useState([
    {
      id: 0,
      filterValue: FilterCompanyEnum.ALL,
      label: 'Alphabetical',
      isActive: true,
    },
    {
      id: 1,
      filterValue: FilterCompanyEnum.MOST_RECENT,
      label: 'All Retros',
      isActive: false,
    },
    {
      id: 2,
      filterValue: FilterCompanyEnum.MOST_REVIEWED,
      label: 'Most Retros',
      isActive: false,
    },
  ]);

  const handleSnackBarClose = () => {
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  // Handle follow/unfollow company
  const handleFollowClick = useCallback(
    async (company: Company) => {
      if (!user || !user.idToken) {
        // Store intent to follow company after onboarding
        storeOnboardingIntent({
          action: OnboardingAction.FollowCompany,
          returnUrl: window.location.pathname,
          companyId: String(company.id),
          companyName: company.companyName,
        });
        setPendingFollowCompany(company);
        setAuthModalAction('follow');
        setShowAuthModal(true);
        return;
      }

      try {
        const isCompanyFollowed = user?.followedCompanies?.find(
          (userFollowedCompany) => userFollowedCompany.id === company.id,
        );

        const response = await fetch(
          isCompanyFollowed
            ? `${import.meta.env.VITE_API_BASE_URL}/users/unfollow-company`
            : `${import.meta.env.VITE_API_BASE_URL}/users/follow-company`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: user.idToken,
            },
            body: JSON.stringify({
              companyId: company.id,
              userId: user.id,
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to update follow status');
        }

        // Update local user state to reflect the follow/unfollow change
        await updateUser((prevUser) => {
          if (!prevUser) {
            return prevUser;
          }

          const followedCompanies = prevUser.followedCompanies || [];

          if (isCompanyFollowed) {
            // Remove company from followed list
            return {
              ...prevUser,
              followedCompanies: followedCompanies.filter((fc) => fc.id !== company.id),
            };
          }

          // Add company to followed list
          return {
            ...prevUser,
            followedCompanies: [...followedCompanies, company],
          };
        });

        setSnackbarConfig({
          open: true,
          type: 'success',
          message: isCompanyFollowed ? `Unfollowed ${company.companyName}` : `Following ${company.companyName}`,
        });
      } catch (error) {
        log.error('Error updating follow status:', error);
        setSnackbarConfig({
          open: true,
          type: 'error',
          message: 'Failed to update follow status',
        });
      }
    },
    [user, updateUser],
  );

  const handleAuthSuccessForFollow = async () => {
    setShowAuthModal(false);
    // Note: onboarding will handle the follow action
    // No need to do anything here - just close the modal
    setPendingFollowCompany(null);
  };

  const handleAuthSuccessForReview = async () => {
    setShowAuthModal(false);
    // Note: onboarding will handle redirecting back here with the addCompanyReview flag
    // The intent is already stored before showing the auth modal
  };

  const handleAuthSuccessForAddCompany = async () => {
    setShowAuthModal(false);
    // Note: onboarding will handle redirecting back here with the addCompany flag
    // The intent is already stored before showing the auth modal
  };

  // Filter configuration
  const filterConfigs: FilterConfig[] = [
    {
      id: 'companyName',
      label: 'Company Name',
      type: 'textfield',
      size: 'small',
      placeholder: 'Filter by company name',
      testId: 'company-name-filter',
      debounceMillis: 300, // Custom debounce delay for company name search
    },
    {
      id: 'industry',
      label: 'Sector / Industry',
      type: 'select',
      autocomplete: true,
      multiple: true,
      clearable: false,
      size: 'small',
      options: industries
        .sort((a, b) => {
          if (a.tier === b.tier) {
            return a.name.localeCompare(b.name);
          }
          return gicsTierRank(a.tier) - gicsTierRank(b.tier);
        })
        .map((gics) => ({
          value: gics.code,
          name: gics.name,
          group: gics.tier || 'Other',
        })),
      testId: 'gics-filter-select',
    },
  ];

  // Sort configuration
  const sortOptions: SortOption[] = [
    { id: 'companyName', label: 'Company Name' },
    { id: 'createdAt', label: 'Date Created' },
    { id: 'reviewCount', label: 'Review Count' },
  ];

  // Load more companies for infinite scroll
  const loadMoreCompanies = useCallback(async () => {
    if (!hasNextPage || isLoadingMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      const activeFilter = filters.find((filter) => filter.isActive)?.filterValue || FilterCompanyEnum.ALL;
      const nextOffset = currentOffset + PAGE_SIZE;

      const searchCompaniesDTO: SearchCompaniesDTO = {
        limit: PAGE_SIZE,
        offset: nextOffset,
        orderBy: sortBy,
        orderDirection: sortDirection.toLowerCase(),
      };

      if (debouncedSearchTerm.trim() !== '') {
        searchCompaniesDTO.searchTerm = debouncedSearchTerm;
      }

      if (
        currentFilters.companyName &&
        typeof currentFilters.companyName === 'string' &&
        currentFilters.companyName.trim() !== ''
      ) {
        searchCompaniesDTO.searchTerm = currentFilters.companyName;
      }

      if (currentFilters.industry && Array.isArray(currentFilters.industry) && currentFilters.industry.length > 0) {
        searchCompaniesDTO.gicsCode = currentFilters.industry;
      }

      if (activeFilter === FilterCompanyEnum.MOST_RECENT) {
        searchCompaniesDTO.orderBy = 'createdAt';
        searchCompaniesDTO.orderDirection = 'desc';
      }
      if (activeFilter === FilterCompanyEnum.MOST_REVIEWED) {
        searchCompaniesDTO.orderBy = 'reviewCount';
        searchCompaniesDTO.orderDirection = 'desc';
      }

      const response = await fetchCompanies(searchCompaniesDTO);

      // Append new companies to existing list, deduplicating by ID
      setCompanies((prev) => {
        const existingIds = new Set(prev.map((c) => c.id));
        const uniqueNewCompanies = response.results.filter((c) => !existingIds.has(c.id));
        return [...prev, ...uniqueNewCompanies];
      });
      setCurrentOffset(nextOffset);
      setHasNextPage(response.meta.hasMore);
    } catch (error) {
      log.error('Error loading more companies:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    currentOffset,
    PAGE_SIZE,
    filters,
    debouncedSearchTerm,
    currentFilters,
    sortBy,
    sortDirection,
  ]);

  // Predictively load next page of companies for prefetching
  const prefetchNextPage = useCallback(async () => {
    if (!hasNextPage || isLoadingMore || nextPageCompanies.length > 0) {
      return; // Don't prefetch if already prefetched or loading
    }

    try {
      const activeFilter = filters.find((filter) => filter.isActive)?.filterValue || FilterCompanyEnum.ALL;
      const nextOffset = currentOffset + PAGE_SIZE;

      const searchCompaniesDTO: SearchCompaniesDTO = {
        limit: PAGE_SIZE,
        offset: nextOffset,
        orderBy: sortBy,
        orderDirection: sortDirection.toLowerCase(),
      };

      if (debouncedSearchTerm.trim() !== '') {
        searchCompaniesDTO.searchTerm = debouncedSearchTerm;
      }

      if (
        currentFilters.companyName &&
        typeof currentFilters.companyName === 'string' &&
        currentFilters.companyName.trim() !== ''
      ) {
        searchCompaniesDTO.searchTerm = currentFilters.companyName;
      }

      if (currentFilters.industry && Array.isArray(currentFilters.industry) && currentFilters.industry.length > 0) {
        searchCompaniesDTO.gicsCode = currentFilters.industry;
      }

      if (activeFilter === FilterCompanyEnum.MOST_RECENT) {
        searchCompaniesDTO.orderBy = 'createdAt';
        searchCompaniesDTO.orderDirection = 'desc';
      }
      if (activeFilter === FilterCompanyEnum.MOST_REVIEWED) {
        searchCompaniesDTO.orderBy = 'reviewCount';
        searchCompaniesDTO.orderDirection = 'desc';
      }

      const response = await fetchCompanies(searchCompaniesDTO);
      setNextPageCompanies(response.results);
    } catch (error) {
      log.error('Error prefetching next page:', error);
    }
  }, [
    hasNextPage,
    isLoadingMore,
    nextPageCompanies.length,
    currentOffset,
    PAGE_SIZE,
    filters,
    debouncedSearchTerm,
    currentFilters,
    sortBy,
    sortDirection,
  ]);

  // Trigger predictive prefetching when companies load or offset changes
  useEffect(() => {
    if (companies.length >= PAGE_SIZE * 0.5 && hasNextPage && !isLoadingMore && nextPageCompanies.length === 0) {
      // Start prefetching when user has loaded at least half a page
      const timer = setTimeout(prefetchNextPage, 500);
      return () => clearTimeout(timer);
    }
  }, [
    companies.length,
    currentOffset,
    PAGE_SIZE,
    hasNextPage,
    isLoadingMore,
    nextPageCompanies.length,
    prefetchNextPage,
  ]);

  // Handle infinite scroll
  const handleRowScrollEnd = useCallback(
    async (_params: GridRowScrollEndParams) => {
      if (!hasNextPage || isLoadingMore || loading) {
        return;
      }

      setIsLoadingMore(true);

      try {
        // If we have prefetched data, use it immediately
        if (nextPageCompanies.length > 0) {
          // Check for duplicates before appending - use company IDs for deduplication
          setCompanies((prev) => {
            const existingIds = new Set(prev.map((c) => c.id));
            const uniqueNewCompanies = nextPageCompanies.filter((c) => !existingIds.has(c.id));
            return [...prev, ...uniqueNewCompanies];
          });
          setCurrentOffset((prev) => prev + PAGE_SIZE);
          setNextPageCompanies([]); // Clear prefetched data
          // Note: Don't call prefetchNextPage here - let the useEffect handle it
          // to avoid stale closure issues with currentOffset
        } else {
          // Fallback to regular loading
          await loadMoreCompanies();
        }
      } finally {
        setIsLoadingMore(false);
      }
    },
    [hasNextPage, isLoadingMore, loading, nextPageCompanies, loadMoreCompanies, PAGE_SIZE],
  );

  // Reset data when filters change
  useEffect(() => {
    setCurrentOffset(0);
    setHasNextPage(true);
    setCompanies([]);
    setNextPageCompanies([]); // Clear prefetched data when filters change
  }, [currentFilters, sortBy, sortDirection]);

  // Handle filter changes
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    setCurrentFilters((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  // Handle sort changes
  const handleSortChange = (orderBy: string, orderDirection: 'ASC' | 'DESC') => {
    setSortBy(orderBy);
    setSortDirection(orderDirection);
  };

  // Handle clear all filters
  const handleClearAllFilters = () => {
    setCurrentFilters({
      industry: [],
      companyName: '',
    });
  };

  // Handle industry click from table cell
  const handleIndustryClick = useCallback(
    (industryName: string) => {
      const gicsCode = industries.find((i) => i.name === industryName)?.code;
      if (!gicsCode) {
        return;
      }

      setCurrentFilters((prev) => {
        const currentIndustryFilters = (prev.industry as string[]) || [];
        if (currentIndustryFilters.includes(gicsCode)) {
          return prev;
        }
        return {
          ...prev,
          industry: [...currentIndustryFilters, gicsCode],
        };
      });
    },
    [industries],
  );

  // DataGrid column definitions
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'Logo',
        headerName: '',
        width: 80,
        display: 'flex',
        align: 'center',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Link
            to={`/company/${encodeURIComponent(params.row.companyName!)}`}
            className="company-search-page__cell-link"
          >
            <CompanyLogo
              companyName={params.row.companyName ?? ''}
              companyId={params.row.id}
              companyUrl={params.row.url}
              alt={params.row.companyName}
              lazy={false}
              sx={{ width: 40, height: 40, cursor: 'pointer' }}
              onClick={() => navigate(`/company/${encodeURIComponent(params.row.companyName!)}`)}
            />
          </Link>
        ),
      },
      {
        field: 'companyName',
        headerName: 'Company',
        display: 'flex',
        sortable: false,
        filterable: false,
        flex: 1,
        renderCell: (params) => (
          <Link to={`/company/${encodeURIComponent(params.value!)}`} className="company-search-page__cell-link">
            <div className="company-search-page__company-cell">
              <p className="company-search-page__company-name">{params.value}</p>
              {params.row.stockTicker && (
                <p className="company-search-page__company-ticker">{params.row.stockTicker}</p>
              )}
            </div>
          </Link>
        ),
      },
      {
        field: 'gicsIndustry',
        headerName: 'Industry',
        minWidth: 200,
        display: 'flex',
        sortable: false,
        filterable: false,
        renderCell: (params) =>
          params.value ? (
            <span
              className="company-search-page__industry-link"
              onClick={(e) => {
                e.stopPropagation();
                handleIndustryClick(params.value as string);
              }}
            >
              {params.value}
            </span>
          ) : null,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        display: 'flex',
        minWidth: 270,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const company = params.row as Company;
          const isCompanyFollowed = user?.followedCompanies?.find(
            (userFollowedCompany) => userFollowedCompany.id === company.id,
          );
          const showBounty = company.createdBy === null || company.tags?.some((tag) => tag.name === 'Fortune 500');

          return (
            <div className="company-search-page__actions">
              <Link
                to={`/company/${encodeURIComponent(params.row.companyName!)}`}
                className="company-search-page__cell-link"
              >
                <Chip label="View" size="small" clickable className="company-search-page__chip-view" />
              </Link>
              <Chip
                data-testid="follow-chip"
                label={isCompanyFollowed ? 'Following' : 'Follow'}
                size="small"
                clickable
                onClick={(e) => {
                  e.stopPropagation();
                  handleFollowClick(company);
                }}
                className={
                  isCompanyFollowed
                    ? 'company-search-page__chip-follow company-search-page__chip-follow--active'
                    : 'company-search-page__chip-follow'
                }
              />
              {showBounty && (
                <Link
                  to={`/company/${encodeURIComponent(params.row.companyName!)}`}
                  className="company-search-page__cell-link"
                >
                  <Tooltip title="$100 payment available for published deal retros for this company.">
                    <Chip label="$100" size="small" className="company-search-page__chip-bounty" />
                  </Tooltip>
                </Link>
              )}
            </div>
          );
        },
      },
    ],
    [navigate, user?.followedCompanies, handleFollowClick, handleIndustryClick],
  );

  const memoizedFetchCompanies = useMemo(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const activeFilter = filters.find((filter) => filter.isActive)?.filterValue || FilterCompanyEnum.ALL;

        const searchCompaniesDTO: SearchCompaniesDTO = {
          limit: PAGE_SIZE,
          offset: 0, // Always start from beginning for initial load
          orderBy: sortBy,
          orderDirection: sortDirection.toLowerCase(),
        };

        if (debouncedSearchTerm.trim() !== '') {
          searchCompaniesDTO.searchTerm = debouncedSearchTerm;
        }

        // Add company name filter from the filters component
        if (
          currentFilters.companyName &&
          typeof currentFilters.companyName === 'string' &&
          currentFilters.companyName.trim() !== ''
        ) {
          // If both search term and name filter exist, combine them or prioritize one
          // Here we'll use the name filter if it exists, otherwise fall back to search term
          searchCompaniesDTO.searchTerm = currentFilters.companyName;
        }

        // Add GICS filter
        if (currentFilters.industry && Array.isArray(currentFilters.industry) && currentFilters.industry.length > 0) {
          searchCompaniesDTO.gicsCode = currentFilters.industry;
        }

        // Legacy filter handling (to maintain existing functionality)
        if (activeFilter === FilterCompanyEnum.MOST_RECENT) {
          searchCompaniesDTO.orderBy = 'createdAt';
          searchCompaniesDTO.orderDirection = 'desc';
        }
        if (activeFilter === FilterCompanyEnum.MOST_REVIEWED) {
          searchCompaniesDTO.orderBy = 'reviewCount';
          searchCompaniesDTO.orderDirection = 'desc';
        }

        const fetchedCompanies = await fetchCompanies(searchCompaniesDTO);

        if (searchCompaniesDTO.searchTerm) {
          trackEvent({
            eventName: 'company_search',
            eventCategory: 'Company Search',
            eventLabel: debouncedSearchTerm,
            eventValue: fetchedCompanies.results.length,
          });
        }

        setCompanies(fetchedCompanies.results);
        setCurrentOffset(0); // Reset offset after initial load
        setHasNextPage(fetchedCompanies.meta.hasMore);
      } catch (error) {
        log.error('Error fetching companies:', error);
        setSnackbarConfig({
          open: true,
          type: 'error',
          message: 'Server error, please try again later',
        });
        setCompanies([]);
        setCurrentOffset(0);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    };

    return fetchData;
  }, [filters, debouncedSearchTerm, currentFilters, sortBy, sortDirection, PAGE_SIZE]);

  useEffect(() => {
    // Only fetch companies after initialization is complete
    if (isInitialized) {
      memoizedFetchCompanies();
    }
  }, [memoizedFetchCompanies, isInitialized]);

  useEffect(() => {
    const completedAction = sessionStorage.getItem('completedOnboardingAction');
    if (completedAction) {
      try {
        const action = JSON.parse(completedAction);

        if (action.type === 'followCompany' && action.companyName) {
          setSnackbarConfig({
            open: true,
            type: 'success',
            message: `Successfully followed ${action.companyName}`,
          });
        } else if (action.type === 'addCompanyFlow') {
          setShowAddCompanyModal(true);
        } else if (action.type === 'addCompanyReview') {
          resetReviewState();
          setIsAddCompany(true);
          setOpenReviewDialog(true);
        }

        sessionStorage.removeItem('completedOnboardingAction');
      } catch {
        setSnackbarConfig({
          open: true,
          type: 'error',
          message: 'Error processing onboarding action',
        });
      }
    }

    // Legacy check for pendingFollowCompany state (in case user follows while already logged in)
    if (user && pendingFollowCompany) {
      const isNowFollowed = user.followedCompanies?.find((fc) => fc.id === pendingFollowCompany.id);

      if (isNowFollowed) {
        setSnackbarConfig({
          open: true,
          type: 'success',
          message: `Successfully followed ${pendingFollowCompany.companyName}`,
        });
        setPendingFollowCompany(null);
      }
    }
  }, [user, user?.followedCompanies, pendingFollowCompany, resetReviewState]);

  const seoTitle = props.embedded ? 'Research Companies' : 'Company Search';
  const seoDescription =
    'Search and research companies to find sales insights, reviews, and intelligence from other sales professionals.';

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        ogImage="https://app.kandirnetwork.io/kandir_logo.png"
        twitterImage="https://app.kandirnetwork.io/kandir_logo.png"
      />

      <div
        className={props.embedded ? 'company-search-page company-search-page--embedded' : 'company-search-page'}
        data-testid="company-search-page"
      >
        {!props.embedded && (
          <header className="company-search-page__header">
            <p className="company-search-page__eyebrow">Discover</p>
            <h1 className="company-search-page__title">Company Search</h1>
            <p className="company-search-page__subtitle">{seoDescription}</p>
          </header>
        )}

        <Filters
          filters={filterConfigs}
          onFilterChange={handleFilterChange}
          onClearAllFilters={handleClearAllFilters}
          sortOptions={sortOptions}
          orderBy={sortBy}
          orderDirection={sortDirection}
          onSortChange={handleSortChange}
          storagePrefix="company-search"
          currentFilters={currentFilters}
          sortSize="small"
          defaultWidth="220px"
        />

        {loading ? (
          <LoadingSpinner variant="circular" size="large" message="Loading, please wait..." fullScreen />
        ) : companies.length ? (
          <Box className="company-search-page__grid">
            <DataGridPro
              columnVisibilityModel={{
                gicsIndustry: !isMobile,
                actions: !isMobile,
              }}
              rows={companies.map((company, index) => ({
                id: company.id || index,
                ...company,
              }))}
              rowHeight={70}
              columns={columns}
              loading={loading || isLoadingMore}
              disableRowSelectionOnClick
              onRowClick={(params: GridRowParams) => {
                navigate(`/company/${encodeURIComponent(params.row.companyName!)}`);
              }}
              onRowsScrollEnd={handleRowScrollEnd}
              scrollEndThreshold={600}
              pagination={false}
              listView={isMobile}
            />
          </Box>
        ) : (
          <div className="company-search-page__empty">
            <h2 className="company-search-page__empty-title">Don't see the company you're looking for?</h2>
            <div className="company-search-page__empty-actions">
              <button
                type="button"
                data-testid="company-search-add-company-and-follow-button"
                className="company-search-page__cta"
                onClick={() => {
                  if (!isLoggedIn) {
                    storeOnboardingIntent({
                      action: OnboardingAction.CreateReview,
                      returnUrl: window.location.pathname,
                      isAddCompanyFlow: true,
                    });
                    setAuthModalAction('addCompany');
                    setShowAuthModal(true);
                  } else {
                    setShowAddCompanyModal(true);
                  }
                }}
              >
                <AddCircleOutline fontSize="small" />
                Add Company & Follow
              </button>
              <span className="company-search-page__empty-divider">or</span>
              <button
                type="button"
                data-testid="company-search-add-to-database-button"
                className="company-search-page__cta company-search-page__cta--ghost"
                onClick={() => {
                  if (!isLoggedIn) {
                    storeOnboardingIntent({
                      action: OnboardingAction.CreateReview,
                      returnUrl: window.location.pathname,
                      isAddCompanyReview: true,
                    });
                    setAuthModalAction('review');
                    setShowAuthModal(true);
                  } else {
                    resetReviewState();
                    setIsAddCompany(true);
                    setOpenReviewDialog(true);
                  }
                }}
              >
                <RateReview fontSize="small" />
                Add Company & Leave a Deal Retro
              </button>
            </div>
          </div>
        )}

        <SignUpOrLogInModal
          open={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setPendingFollowCompany(null);
          }}
          contextMessage={
            authModalAction === 'follow'
              ? `Create an account to follow ${pendingFollowCompany?.companyName || 'this company'}`
              : authModalAction === 'addCompany'
                ? `Create an account to add a company and follow for updates`
                : `Create an account to leave a deal retro`
          }
          handleAuthSuccess={
            authModalAction === 'follow'
              ? handleAuthSuccessForFollow
              : authModalAction === 'addCompany'
                ? handleAuthSuccessForAddCompany
                : handleAuthSuccessForReview
          }
          initialMode="signup"
        />

        <ReviewDialog
          open={openReviewDialog}
          isAddCompany={isAddCompany}
          setOpen={setOpenReviewDialog}
          initialCompanyName={searchTerm}
        />
        <CreateCompanyModal
          open={showAddCompanyModal}
          onClose={() => setShowAddCompanyModal(false)}
          onCompanyCreated={(company) => {
            setSnackbarConfig({
              open: true,
              type: 'success',
              message: `${company.companyName} added successfully!`,
            });
            navigate(`/company/${encodeURIComponent(company.companyName)}`);
            memoizedFetchCompanies();
          }}
        />

        <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </div>
    </>
  );
}

export default CompanySearchPage;
